import { BASE_URL } from '../types/constants';
import type { SiteConfiguration } from '../types/site.types';
import { sanitizeAnalyticsUrl } from '../utils/analytics-url';

export type MarketingConversionKind = 'pageView' | 'signup' | 'subscribe' | 'purchase' | 'lead' | 'search';
const marketingConsentStorageKey = 'combattv_marketing_consent';
const marketingConsentGranted = 'granted';
const pageViewConversionSessionPrefix = 'combattv_google_page_view_conversion';
const analyticsClientEventIdHeader = 'X-Analytics-Client-Event-Id';
const marketingConsentHeader = 'X-Marketing-Consent';

/**
 * Browser event emitted after consent-approved marketing tags have been configured for the current page load.
 */
export const marketingTagsLoadedEventName = 'combattv_marketing_tags_loaded';

export interface MarketingConversionPayload {
    kind: MarketingConversionKind;
    eventId?: string;
    value?: number;
    currency?: string | null;
    orderId?: string | null;
    searchTerm?: string | null;
    pagePath?: string | null;
    pageTitle?: string | null;
    metadata?: Record<string, string | number | boolean | null | undefined>;
}

declare global {
    interface Window {
        dataLayer?: unknown[];
        gtag?: (...args: unknown[]) => void;
        fbq?: (...args: unknown[]) => void;
        __combattvMarketingConfig?: SiteConfiguration;
    }
}

/**
 * Fetches public marketing and contact configuration without exposing any server-side secrets.
 */
export const fetchPublicSiteConfiguration = async (): Promise<SiteConfiguration | null> => {
    const response = await fetch(`${BASE_URL}/v1/site/site-info`);
    if (!response.ok) {
        return null;
    }

    return response.json() as Promise<SiteConfiguration>;
};

const googleConversionLabels: Record<MarketingConversionKind, keyof SiteConfiguration | null> = {
    pageView: 'GoogleAdsPageViewConversionLabel',
    signup: 'GoogleAdsSignupConversionLabel',
    subscribe: 'GoogleAdsSubscribeConversionLabel',
    purchase: 'GoogleAdsPurchaseConversionLabel',
    lead: null,
    search: null
};

const dataLayerEventNames: Record<MarketingConversionKind, string> = {
    pageView: 'combattv_page_view_conversion',
    signup: 'combattv_signup',
    subscribe: 'combattv_subscription',
    purchase: 'combattv_purchase',
    lead: 'combattv_lead',
    search: 'combattv_search'
};

const metaEventNames: Partial<Record<MarketingConversionKind, string>> = {
    signup: 'CompleteRegistration',
    subscribe: 'Subscribe',
    purchase: 'Purchase',
    lead: 'Lead',
    search: 'Search'
};

const compactMetadata = (metadata?: MarketingConversionPayload['metadata']) => {
    if (!metadata) {
        return undefined;
    }

    return Object.entries(metadata).reduce<Record<string, string | number | boolean | null>>((result, [key, value]) => {
        if (value !== undefined) {
            result[key] = value;
        }
        return result;
    }, {});
};

const hasMarketingConsent = (config: SiteConfiguration) => {
    if (!config.RequireMarketingConsent) {
        return true;
    }

    try {
        return window.localStorage.getItem(marketingConsentStorageKey) === marketingConsentGranted;
    } catch {
        return false;
    }
};

const getCurrentPagePath = () => {
    const path = window.location.pathname || '/';
    return sanitizeAnalyticsUrl(`${path}${window.location.search}`) || path;
};

const isPageViewConversionPathEligible = (pagePath: string) => {
    // Google Ads page-view conversions measure public landing traffic, not admin workspace navigation.
    return pagePath !== '/admin' && !pagePath.startsWith('/admin/') && !pagePath.startsWith('/admin?');
};

const getPageViewConversionSessionKey = (pagePath: string) => `${pageViewConversionSessionPrefix}:${pagePath}`;

const hasSentPageViewConversion = (pagePath: string) => {
    try {
        return window.sessionStorage.getItem(getPageViewConversionSessionKey(pagePath)) === 'sent';
    } catch {
        return false;
    }
};

const markPageViewConversionSent = (pagePath: string) => {
    try {
        window.sessionStorage.setItem(getPageViewConversionSessionKey(pagePath), 'sent');
    } catch {
        // Session storage is only used to avoid duplicate conversion events during one browser session.
    }
};

/**
 * Builds backend request headers that let server-side conversion events honor consent and share pixel event ids.
 */
export const buildMarketingAttributionHeaders = (eventId?: string) => {
    const headers: Record<string, string> = {};
    if (eventId) {
        headers[analyticsClientEventIdHeader] = eventId;
    }

    if (typeof window === 'undefined') {
        return headers;
    }

    try {
        if (window.localStorage.getItem(marketingConsentStorageKey) === marketingConsentGranted) {
            headers[marketingConsentHeader] = marketingConsentGranted;
        }
    } catch {
        // Missing storage means no consent marker is sent.
    }

    return headers;
};

/**
 * Sends consent-approved browser conversion events to GTM/gtag/Meta Pixel when those tags are loaded.
 */
export const trackMarketingConversion = (payload: MarketingConversionPayload) => {
    if (typeof window === 'undefined') {
        return;
    }

    const config = window.__combattvMarketingConfig;
    if (!config?.EnableMarketingTags || !hasMarketingConsent(config)) {
        return;
    }

    const metadata = compactMetadata(payload.metadata);
    const pagePath = sanitizeAnalyticsUrl(payload.pagePath);
    const dataLayerPayload = {
        event: dataLayerEventNames[payload.kind],
        eventId: payload.eventId,
        conversionType: payload.kind,
        value: payload.value,
        currency: payload.currency ?? undefined,
        transactionId: payload.orderId ?? undefined,
        searchTerm: payload.searchTerm ?? undefined,
        pagePath: pagePath ?? undefined,
        pageTitle: payload.pageTitle ?? undefined,
        metadata
    };

    window.dataLayer = window.dataLayer ?? [];
    window.dataLayer.push(dataLayerPayload);

    const conversionLabelKey = googleConversionLabels[payload.kind];
    const conversionLabel = conversionLabelKey ? config[conversionLabelKey] : null;
    if (window.gtag && config.GoogleAdsConversionId && conversionLabel) {
        window.gtag('event', 'conversion', {
            send_to: `${config.GoogleAdsConversionId}/${conversionLabel}`,
            value: payload.value,
            currency: payload.currency ?? undefined,
            transaction_id: payload.orderId ?? undefined,
            page_path: pagePath ?? undefined,
            page_title: payload.pageTitle ?? undefined
        });
    }

    const metaEventName = metaEventNames[payload.kind];
    if (window.fbq && config.EnableMetaPixel && metaEventName) {
        const eventParameters = {
            value: payload.value,
            currency: payload.currency ?? undefined,
            order_id: payload.orderId ?? undefined,
            search_string: payload.searchTerm ?? undefined,
            ...metadata
        };
        const eventOptions = payload.eventId ? { eventID: payload.eventId } : undefined;
        if (eventOptions) {
            window.fbq('track', metaEventName, eventParameters, eventOptions);
            return;
        }

        window.fbq('track', metaEventName, eventParameters);
    }
};

/**
 * Sends the configured Google Ads page-view conversion once per browser session per landing path.
 */
export const trackConfiguredPageViewConversion = () => {
    if (typeof window === 'undefined') {
        return;
    }

    const config = window.__combattvMarketingConfig;
    if (!config?.EnableMarketingTags
        || !hasMarketingConsent(config)
        || !config.GoogleAdsConversionId
        || !config.GoogleAdsPageViewConversionLabel) {
        return;
    }

    const pagePath = getCurrentPagePath();
    if (!isPageViewConversionPathEligible(pagePath)) {
        return;
    }

    if (hasSentPageViewConversion(pagePath)) {
        return;
    }

    trackMarketingConversion({
        kind: 'pageView',
        pagePath,
        pageTitle: document.title || null,
        metadata: { pagePath }
    });
    markPageViewConversionSent(pagePath);
};
